import { useRef, useState } from "react";
import { Upload, FileSpreadsheet, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const FIELDS = [
  { key: "name", label: "Customer name", required: true },
  { key: "contact_name", label: "Contact name" },
  { key: "contact_email", label: "Contact email" },
  { key: "contact_phone", label: "Contact phone" },
  { key: "location", label: "Location" },
  { key: "notes", label: "Notes" },
  { key: "status", label: "Status" },
];
const USER_FIELDS = [
  { key: "group", label: "Customer (group by)", required: true },
  { key: "name", label: "User name", required: true },
  { key: "email", label: "User email", required: true },
  { key: "phone", label: "User phone" },
];
const SKIP = "__skip__";
const COMBINE = "__combine__";
const CONCAT_KEYS = ["name", "contact_name"]; // fields that may be built from 2 source columns (e.g. first + last name)

const isCombine = (v) => !!v && typeof v === "object" && v.combine;
const hasMappingValue = (v) => !!v && v !== SKIP && (!isCombine(v) || (v.col1 && v.col2));

// When a file has separate "First name"/"Last name"-style columns, default the combine
// picker to them instead of just the first two columns.
const guessCombo = (columns) => {
  const first = columns.find((c) => /first/i.test(c));
  const last = columns.find((c) => /last/i.test(c));
  if (first && last) return [first, last];
  return [columns[0] || "", columns[1] || columns[0] || ""];
};

// Iter 304 — some exports (e.g. a phone-system user export) are one row per USER
// with a column naming the customer each user belongs to (e.g. "domain"), rather
// than one row per customer. Best-effort default mapping for that layout.
const guessUserMapping = (columns) => {
  const findCol = (hints) => columns.find((c) => hints.some((h) => c.toLowerCase().includes(h)));
  const group = findCol(["domain", "company", "customer", "account", "tenant", "organization"]);
  const email = findCol(["email", "e-mail", "mail"]);
  const phone = findCol(["phone", "mobile", "cell"]);
  const [c1, c2] = guessCombo(columns);
  const m = {};
  if (group) m.group = group;
  if (email) m.email = email;
  if (phone) m.phone = phone;
  if (c1 && c2 && c1 !== c2) m.name = { combine: true, col1: c1, col2: c2 };
  else if (c1) m.name = c1;
  return m;
};

const fieldValue = (row, mapping, key) => {
  const m = mapping[key];
  if (!m || m === SKIP) return "";
  if (isCombine(m)) {
    const a = (row[m.col1] || "").toString().trim();
    const b = (row[m.col2] || "").toString().trim();
    return [a, b].filter(Boolean).join(" ");
  }
  return row[m] || "";
};

export const ImportCustomersDialog = ({ open, onOpenChange, partners, isPartner, onDone }) => {
  const inputRef = useRef(null);
  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("customers"); // "customers" | "users"
  const [parentOrg, setParentOrg] = useState("none");
  const [result, setResult] = useState(null);

  const activeFields = mode === "users" ? USER_FIELDS : FIELDS;
  const reset = () => { setStep("upload"); setPreview(null); setMapping({}); setMode("customers"); setParentOrg("none"); setResult(null); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const switchMode = (newMode) => {
    setMode(newMode);
    if (preview) setMapping(newMode === "users" ? guessUserMapping(preview.columns) : (preview.suggested_mapping || {}));
  };

  const pickFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/organizations/import-preview", { filename: file.name, content_b64: b64 });
      if (!data.rows.length) { toast.error("The file has no data rows"); return; }
      setPreview(data);
      setMode("customers");
      setMapping(data.suggested_mapping || {});
      setStep("map");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not read file"); }
    finally { setBusy(false); }
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const rows = preview.rows.map((r) => Object.fromEntries(activeFields.map((f) => [f.key, fieldValue(r, mapping, f.key)])));
      const parent_org_id = parentOrg === "none" ? null : parentOrg;
      if (mode === "users") {
        const { data } = await api.post("/organizations/import-users", { parent_org_id, rows });
        setResult({ ...data, mode: "users" });
      } else {
        const { data } = await api.post("/organizations/import", { parent_org_id, rows });
        setResult({ ...data, mode: "customers" });
      }
      setStep("done");
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };

  const mappedPreview = (preview?.rows || []).slice(0, 5).map((r) =>
    Object.fromEntries(activeFields.map((f) => [f.key, fieldValue(r, mapping, f.key)])));
  const canImport = mode === "users"
    ? ["group", "name", "email"].every((k) => hasMappingValue(mapping[k]))
    : hasMappingValue(mapping.name);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="import-customers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> Import customers</DialogTitle>
          <DialogDescription>Bulk-load customer accounts from a CSV or Excel file.</DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/60 py-10 text-center"
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}>
            {busy ? <Loader2 className="h-7 w-7 animate-spin text-primary" /> : <Upload className="h-7 w-7 text-slate-400" />}
            <p className="mt-2 text-sm text-slate-600">Drag &amp; drop a CSV or Excel file here, or</p>
            <Button variant="outline" size="sm" className="mt-2" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="import-browse-button">Browse files</Button>
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-file-input"
              onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
            <p className="mt-2 text-[11px] text-slate-400">.csv, .xlsx or .xls · max 5 MB · a "name" column is required</p>
          </div>
        )}

        {step === "map" && preview && (
          <div className="space-y-4">
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="import-mode-toggle">
              <button type="button" onClick={() => switchMode("customers")} data-testid="import-mode-customers"
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition ${mode === "customers" ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                Each row is a customer
              </button>
              <button type="button" onClick={() => switchMode("users")} data-testid="import-mode-users"
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition ${mode === "users" ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                Each row is a user, grouped into a customer
              </button>
            </div>
            <p className="text-xs text-slate-500">
              {preview.total_rows} row(s) found{preview.total_rows > preview.rows.length ? ` — only the first ${preview.rows.length} will be imported` : ""}.{" "}
              {mode === "users"
                ? "One customer account is created per unique value in \"Customer (group by)\" (existing customers with a matching name are reused); every row becomes a user inside it."
                : "Match your file's columns to customer fields:"}
            </p>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {activeFields.map((f) => {
                const val = mapping[f.key];
                const canCombine = CONCAT_KEYS.includes(f.key);
                const combining = isCombine(val);
                return (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
                    <Select
                      value={combining ? COMBINE : (val || SKIP)}
                      onValueChange={(v) => setMapping((m) => ({
                        ...m,
                        [f.key]: v === COMBINE ? { combine: true, ...Object.fromEntries(["col1", "col2"].map((k, i) => [k, guessCombo(preview.columns)[i]])) } : v,
                      }))}
                    >
                      <SelectTrigger data-testid={`import-map-${f.key}`} className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SKIP}>— skip —</SelectItem>
                        {canCombine && <SelectItem value={COMBINE} data-testid={`import-map-combine-${f.key}`}>Combine two columns…</SelectItem>}
                        {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {combining && (
                      <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 p-1.5" data-testid={`import-combine-row-${f.key}`}>
                        <Select value={val.col1} onValueChange={(c1) => setMapping((m) => ({ ...m, [f.key]: { ...val, col1: c1 } }))}>
                          <SelectTrigger className="h-7 text-xs" data-testid={`import-combine-col1-${f.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-xs text-slate-400">+</span>
                        <Select value={val.col2} onValueChange={(c2) => setMapping((m) => ({ ...m, [f.key]: { ...val, col2: c2 } }))}>
                          <SelectTrigger className="h-7 text-xs" data-testid={`import-combine-col2-${f.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                );
              })}
              {!isPartner && (
                <div className="space-y-1">
                  <Label className="text-xs">Parent partner (all rows)</Label>
                  <Select value={parentOrg} onValueChange={setParentOrg}>
                    <SelectTrigger data-testid="import-parent-select" className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {(partners || []).map((p) => <SelectItem key={p.org_id} value={p.org_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-100">
              <table className="w-full text-left text-xs" data-testid="import-preview-table">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>{activeFields.slice(0, 5).map((f) => <th key={f.key} className="px-2 py-1.5 font-medium">{f.label}</th>)}</tr>
                </thead>
                <tbody>
                  {mappedPreview.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      {activeFields.slice(0, 5).map((f) => <td key={f.key} className="max-w-[140px] truncate px-2 py-1.5 text-slate-700">{r[f.key] || <span className="text-slate-300">—</span>}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" size="sm" onClick={reset} disabled={busy}><ArrowLeft className="h-4 w-4" /> Back</Button>
              <Button onClick={runImport} disabled={busy || !canImport} data-testid="import-run-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</> : `Import ${preview.total_rows} row(s)`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
              <CheckCircle2 className="h-4 w-4" />
              {result.mode === "users"
                ? `${result.created_users} user(s) created across ${result.created_customers} customer account(s)${result.skipped.length > 0 ? `, ${result.skipped.length} skipped` : ""}.`
                : `${result.created} customer account(s) created${result.skipped.length > 0 ? `, ${result.skipped.length} skipped` : ""}.`}
            </div>
            {result.skipped.length > 0 && (
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-slate-100 p-2">
                {result.skipped.map((s, i) => (
                  <p key={i} className="text-xs text-slate-500">Row {s.row}{s.name ? ` — ${s.name}` : ""}: <span className="text-amber-600">{s.reason}</span></p>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => close(false)} data-testid="import-close-button">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
